import { Box } from "@mui/material";
import { Pill, Tooltip } from "@procore/core-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { isDebugEnabled, log } from "utils/log";

import { isProcoreStaging } from "./isProcoreStaging";
import { type MfeOverride, MfeOverrides } from "./MfeOverrides";

const l = log.rcToggler;

const LIB_DEPLOY_TYPE = Object.freeze({
    RELEASE_CANDIDATE: "release-candidate",
    PRODUCTION: "production",
    UNKNOWN: "unknown",
});
type LibDeployType = (typeof LIB_DEPLOY_TYPE)[keyof typeof LIB_DEPLOY_TYPE];

const MFE_OVERRIDES: MfeOverride[] = [
    { name: "documentViewerApp", url: "https://bim-wv-sdk-rc.document-viewer-ui-service.pages.dev/" },
];

export function ReleaseCandidateToggler() {
    const [value, setValue] = useState<LibDeployType>(LIB_DEPLOY_TYPE.PRODUCTION);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (MfeOverrides.hasAllExactOverrides(MFE_OVERRIDES)) {
            // If all RC overrides are present, we're in RC mode
            setValue(LIB_DEPLOY_TYPE.RELEASE_CANDIDATE);
        } else if (MfeOverrides.hasSomeOverridesMatchingName(MFE_OVERRIDES)) {
            // If some (but not all) overrides are present, but may point to
            // different URLs, we _may_ be in RC mode but it depends which MFE we're
            // on so just say 'unknown'.
            setValue(LIB_DEPLOY_TYPE.UNKNOWN);
        }
    }, [setValue]);
    const onClick = useCallback(() => {
        switch (value) {
            case LIB_DEPLOY_TYPE.UNKNOWN:
            case LIB_DEPLOY_TYPE.PRODUCTION: {
                setLoading(true);
                const newOverrides = MfeOverrides.addOverrides(MFE_OVERRIDES);
                l.debug(
                    "Switching from Production to Release Candidate. New overrides:\n",
                    JSON.stringify(newOverrides, null, 2),
                );
                break;
            }
            case LIB_DEPLOY_TYPE.RELEASE_CANDIDATE: {
                setLoading(true);
                const newOverrides = MfeOverrides.removeOverrides(MFE_OVERRIDES);
                l.debug(
                    "Switching from Release Candidate to Production. New overrides:\n",
                    JSON.stringify(newOverrides, null, 2),
                );
                break;
            }
            default:
                throw new Error(`Unknown LIB_VERSION: ${value}`);
        }

        window.location.reload();
    }, [value]);
    const color: Parameters<typeof Pill>[0]["color"] = useMemo(() => {
        if (loading) {
            return "gray";
        }
        if (value === LIB_DEPLOY_TYPE.PRODUCTION) {
            return "green";
        }
        if (value === LIB_DEPLOY_TYPE.UNKNOWN) {
            return "yellow";
        }
        return "magenta";
    }, [loading, value]);

    if (!(isProcoreStaging() || isDebugEnabled(l.namespace))) {
        return;
    }
    let message;
    let tooltipMessage;
    if (loading) {
        message = "Reloading...";
    } else if (value === LIB_DEPLOY_TYPE.PRODUCTION) {
        message = `PRODUCTION - ${__LIB_VERSION__}`;
        tooltipMessage = "Currently using the build that's on Production. Click to use the Release Candidate build";
    } else if (value === LIB_DEPLOY_TYPE.RELEASE_CANDIDATE) {
        message = `RELEASE CANDIDATE - ${__LIB_VERSION__}`;
        tooltipMessage =
            "Currently using the Release Candidate build, which is the latest that's been merged, but not necessarily released. Click to use the Production build";
    } else if (value === LIB_DEPLOY_TYPE.UNKNOWN) {
        message = `CUSTOM - ${__LIB_VERSION__}`;
        tooltipMessage =
            "Currently using a custom build set via an MFE override. Click to use the Release Candidate build";
    } else {
        throw new Error(`Unknown LIB_VERSION: ${value}`);
    }

    const pillElement = (
        <Pill
            color={color}
            style={{
                pointerEvents: "auto",
                cursor: "pointer",
                opacity: loading ? 0.5 : 1,
            }}
            onClick={onClick}
        >
            {message}
        </Pill>
    );
    return (
        <Box
            style={{
                position: "absolute",
                bottom: 8,
                right: 8,
            }}
        >
            {loading ? (
                pillElement
            ) : (
                <Tooltip overlay={!loading ? tooltipMessage : undefined} trigger="hover" placement="top">
                    {pillElement}
                </Tooltip>
            )}
        </Box>
    );
}
